import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  Warning,
  People,
  CalendarToday
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface ForecastDataPoint {
  date: string;
  actual?: number;
  predicted: number;
  lower_bound: number;
  upper_bound: number;
}

interface ChurnRiskCustomer {
  customer_id: number;
  customer_name: string;
  email: string;
  churn_probability: number;
  risk_level: 'High' | 'Medium' | 'Low';
  contributing_factors: string[];
  last_interaction_days: number;
}

interface StaffingForecast {
  date: string;
  forecasted_volume: number;
  agents_required: number;
  current_agents: number;
  gap: number;
}

interface PredictiveData {
  volume_forecast: ForecastDataPoint[];
  churn_risks: ChurnRiskCustomer[];
  staffing_forecast: StaffingForecast[];
  insights: {
    peak_days: string[];
    avg_daily_volume: number;
    trend_direction: 'increasing' | 'decreasing' | 'stable';
    high_risk_customers: number;
  };
}

interface PredictiveAnalyticsProps {
  startDate: string;
  endDate: string;
  forecastDays?: number;
}

const PredictiveAnalytics: React.FC<PredictiveAnalyticsProps> = ({
  startDate,
  endDate,
  forecastDays = 30
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PredictiveData | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchPredictiveData();
  }, [startDate, endDate, forecastDays]);

  const fetchPredictiveData = async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call when backend is ready
      // const response = await advancedReportsAPI.getPredictiveAnalytics({
      //   start_date: startDate,
      //   end_date: endDate,
      //   forecast_days: forecastDays
      // });

      // Mock data matching ADVANCED_ANALYTICS_SCENARIOS.md structure
      const mockForecast: ForecastDataPoint[] = [];
      const today = new Date();

      // Generate 30 days of historical data (actual)
      for (let i = -30; i < 0; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() + i);
        const baseValue = 150 + Math.sin(i / 7) * 30;
        mockForecast.push({
          date: date.toISOString().split('T')[0],
          actual: Math.floor(baseValue + Math.random() * 20),
          predicted: 0,
          lower_bound: 0,
          upper_bound: 0
        });
      }

      // Generate 30 days of forecast data (predicted)
      for (let i = 0; i < forecastDays; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() + i);
        const baseValue = 160 + Math.sin(i / 7) * 30 + (i * 0.5); // Slight upward trend
        mockForecast.push({
          date: date.toISOString().split('T')[0],
          predicted: Math.floor(baseValue),
          lower_bound: Math.floor(baseValue - 25),
          upper_bound: Math.floor(baseValue + 25)
        });
      }

      const mockData: PredictiveData = {
        volume_forecast: mockForecast,
        churn_risks: [
          {
            customer_id: 1,
            customer_name: 'Acme Corp',
            email: 'contact@acme.com',
            churn_probability: 0.85,
            risk_level: 'High',
            contributing_factors: [
              'Multiple unresolved tickets',
              'Declining CSAT scores',
              'Long resolution times'
            ],
            last_interaction_days: 45
          },
          {
            customer_id: 2,
            customer_name: 'TechStart Inc',
            email: 'support@techstart.com',
            churn_probability: 0.72,
            risk_level: 'High',
            contributing_factors: [
              'Frequent escalations',
              'Negative sentiment'
            ],
            last_interaction_days: 30
          },
          {
            customer_id: 3,
            customer_name: 'Global Systems',
            email: 'help@globalsys.com',
            churn_probability: 0.58,
            risk_level: 'Medium',
            contributing_factors: [
              'Slow response times'
            ],
            last_interaction_days: 20
          }
        ],
        staffing_forecast: Array.from({ length: forecastDays }, (_, i) => {
          const date = new Date(today);
          date.setDate(date.getDate() + i);
          const volume = 160 + Math.sin(i / 7) * 30 + (i * 0.5);
          const required = Math.ceil((volume / 20) / 0.8); // 20 conv/agent, 80% utilization
          const current = 8;
          return {
            date: date.toISOString().split('T')[0],
            forecasted_volume: Math.floor(volume),
            agents_required: required,
            current_agents: current,
            gap: required - current
          };
        }),
        insights: {
          peak_days: ['Monday', 'Wednesday'],
          avg_daily_volume: 165,
          trend_direction: 'increasing',
          high_risk_customers: 2
        }
      };

      setData(mockData);
    } catch (err: any) {
      console.error('Failed to fetch predictive analytics:', err);
      setError(err.message || 'Failed to load predictive analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No predictive analytics data available.</Alert>;
  }

  // Forecast chart options
  const forecastChartOptions: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: {
        show: true,
        tools: {
          download: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      zoom: {
        enabled: true
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: [2, 2, 0, 0]
    },
    fill: {
      type: ['solid', 'solid', 'gradient', 'gradient'],
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      categories: data.volume_forecast.map(d => d.date),
      labels: {
        rotate: -45,
        rotateAlways: true
      }
    },
    yaxis: {
      title: {
        text: 'Conversations'
      }
    },
    colors: ['#4caf50', '#1f93ff', '#e3f2fd', '#e3f2fd'],
    legend: {
      position: 'top'
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => val ? `${Math.round(val)} conversations` : ''
      }
    },
    annotations: {
      xaxis: [
        {
          x: new Date().toISOString().split('T')[0],
          borderColor: '#f44336',
          label: {
            style: {
              color: '#fff',
              background: '#f44336'
            },
            text: 'Today'
          }
        }
      ]
    }
  };

  const forecastSeries = [
    {
      name: 'Actual',
      type: 'line',
      data: data.volume_forecast.map(d => d.actual || null)
    },
    {
      name: 'Predicted',
      type: 'line',
      data: data.volume_forecast.map(d => d.predicted || null)
    },
    {
      name: 'Upper Bound',
      type: 'area',
      data: data.volume_forecast.map(d => d.upper_bound || null)
    },
    {
      name: 'Lower Bound',
      type: 'area',
      data: data.volume_forecast.map(d => d.lower_bound || null)
    }
  ];

  const getRiskColor = (level: string) => {
    if (level === 'High') return 'error';
    if (level === 'Medium') return 'warning';
    return 'success';
  };

  return (
    <Box>
      {/* Key Insights */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Trend Direction
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp color={data.insights.trend_direction === 'increasing' ? 'error' : 'success'} />
                <Typography variant="h5" textTransform="capitalize">
                  {data.insights.trend_direction}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Next {forecastDays} days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Avg Daily Volume
              </Typography>
              <Typography variant="h4" color="primary">
                {data.insights.avg_daily_volume}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Forecasted conversations/day
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                High-Risk Customers
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Warning color="error" />
                <Typography variant="h4" color="error">
                  {data.insights.high_risk_customers}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Churn probability &gt; 70%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Peak Days
              </Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {data.insights.peak_days.map((day) => (
                  <Chip key={day} label={day} size="small" color="primary" />
                ))}
              </Box>
              <Typography variant="caption" color="textSecondary">
                Highest conversation volume
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for Different Predictions */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Volume Forecast" icon={<TrendingUp />} iconPosition="start" />
          <Tab label="Churn Risk" icon={<Warning />} iconPosition="start" />
          <Tab label="Staffing Forecast" icon={<People />} iconPosition="start" />
        </Tabs>

        {/* Volume Forecast Tab */}
        {activeTab === 0 && (
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Conversation Volume Forecast ({forecastDays} Days)
            </Typography>
            <Typography variant="caption" color="textSecondary" paragraph>
              Time series prediction with confidence intervals using historical patterns and seasonality
            </Typography>
            <Chart
              options={forecastChartOptions}
              series={forecastSeries}
              type="line"
              height={400}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">📈 Forecast Insights</Typography>
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li>
                  <Typography variant="caption">
                    Expected {data.insights.trend_direction === 'increasing' ? 'increase' : 'decrease'} in conversation volume over the next {forecastDays} days
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    Peak volume expected on {data.insights.peak_days.join(' and ')}
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    Average daily volume forecast: {data.insights.avg_daily_volume} conversations
                  </Typography>
                </li>
              </ul>
            </Alert>
          </Box>
        )}

        {/* Churn Risk Tab */}
        {activeTab === 1 && (
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Customer Churn Risk Prediction
            </Typography>
            <Typography variant="caption" color="textSecondary" paragraph>
              AI-powered prediction based on support interactions, sentiment, and engagement patterns
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell align="center">Churn Risk</TableCell>
                    <TableCell align="center">Risk Level</TableCell>
                    <TableCell>Contributing Factors</TableCell>
                    <TableCell align="right">Last Interaction</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.churn_risks.map((customer) => (
                    <TableRow key={customer.customer_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {customer.customer_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {customer.email}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box>
                          <Typography variant="body2" fontWeight="bold" color="error">
                            {(customer.churn_probability * 100).toFixed(0)}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={customer.churn_probability * 100}
                            color="error"
                            sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={customer.risk_level}
                          size="small"
                          color={getRiskColor(customer.risk_level) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {customer.contributing_factors.map((factor, idx) => (
                            <Chip
                              key={idx}
                              label={factor}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">
                          {customer.last_interaction_days} days ago
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">⚠️ Recommended Actions</Typography>
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li>
                  <Typography variant="caption">
                    Immediately reach out to {data.churn_risks.filter(c => c.risk_level === 'High').length} high-risk customers
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    Review and resolve outstanding issues for at-risk accounts
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    Schedule check-in calls with customers showing declining engagement
                  </Typography>
                </li>
              </ul>
            </Alert>
          </Box>
        )}

        {/* Staffing Forecast Tab */}
        {activeTab === 2 && (
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Staffing Requirements Forecast
            </Typography>
            <Typography variant="caption" color="textSecondary" paragraph>
              Agent capacity planning based on predicted conversation volume (20 conv/agent/day, 80% utilization)
            </Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Forecasted Volume</TableCell>
                    <TableCell align="right">Agents Required</TableCell>
                    <TableCell align="right">Current Agents</TableCell>
                    <TableCell align="center">Gap</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.staffing_forecast.slice(0, 14).map((day) => (
                    <TableRow
                      key={day.date}
                      sx={{
                        bgcolor: day.gap > 0 ? '#ffebee' : day.gap < -1 ? '#e8f5e9' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday fontSize="small" color="action" />
                          {new Date(day.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{day.forecasted_volume}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={day.agents_required}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{day.current_agents}</TableCell>
                      <TableCell align="center">
                        {day.gap > 0 ? (
                          <Chip
                            label={`-${day.gap}`}
                            size="small"
                            color="error"
                            icon={<Warning />}
                          />
                        ) : day.gap < -1 ? (
                          <Chip
                            label={`+${Math.abs(day.gap)}`}
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Chip label="OK" size="small" color="default" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">💡 Capacity Planning Insights</Typography>
              <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                <li>
                  <Typography variant="caption">
                    Staffing shortage expected on {data.staffing_forecast.filter(d => d.gap > 0).length} days in the next 2 weeks
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    Consider hiring {Math.max(...data.staffing_forecast.map(d => d.gap))} additional agents to meet peak demand
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    Schedule part-time or flex agents for high-volume days
                  </Typography>
                </li>
              </ul>
            </Alert>
          </Box>
        )}
      </Paper>

      {/* Predictive Model Info */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom>
          🤖 About Predictive Models
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Volume Forecasting
            </Typography>
            <Typography variant="caption" display="block" paragraph>
              Uses time series analysis (Prophet algorithm) with weekly and yearly seasonality patterns.
              Trained on 90+ days of historical data.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Churn Prediction
            </Typography>
            <Typography variant="caption" display="block" paragraph>
              Random Forest classifier analyzing 7+ features including CSAT, resolution times, sentiment, and engagement patterns.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Staffing Optimization
            </Typography>
            <Typography variant="caption" display="block" paragraph>
              Capacity model based on forecasted volume, agent productivity benchmarks, and target utilization rates.
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PredictiveAnalytics;
